import { useEffect, useMemo, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import clsx from 'clsx'
import { usePreventWheelPropagation } from '@/hooks/usePreventWheelPropagation'
// Straight from react-i18next, not '@/i18n': that barrel pulls in the i18n
// bootstrap, which breaks any suite that partially mocks react-i18next — and
// this component is now imported by every card preview.
import { useTranslation } from 'react-i18next'
import type { PreviewMatch } from './types'

/** Rows rendered before the list truncates. Overflow is stated, never silent. */
export const DEFAULT_MAX_ROWS = 500

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Mark every occurrence of `query` inside `snippet`. */
function markQuery(snippet: string, query: string) {
  const term = query.trim()
  if (!term) return snippet
  const parts = snippet.split(new RegExp(`(${escapeRegex(term)})`, 'gi'))
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <mark
        key={i}
        className="bg-yellow-200 dark:bg-yellow-500/40 text-inherit rounded-sm px-0.5"
      >
        {part}
      </mark>
    ) : (
      part
    )
  )
}

export interface PreviewMatchListProps {
  matches: PreviewMatch[]
  /** Index into `matches` of the row the viewport is currently showing. */
  activeIndex: number
  /** The live query — used to mark the hit inside each snippet. */
  query: string
  onSelect: (index: number) => void
  isSearching?: boolean
  /** Extra caveat for the footer — e.g. a surface that capped its own lookup.
   *  Takes precedence over this component's own truncation notice, because a
   *  caller-side cap means `matches.length` is not the real total. */
  footnote?: string
  /** Real match count when the caller passed a capped `matches` list. */
  totalCount?: number
  className?: string
}

/**
 * FS-657: every match in the document, listed at once.
 *
 * The board card preview used to surface matches one at a time ("3 of 12"),
 * so the other nine were invisible until you clicked through them. This lists
 * all of them, grouped by location, and jumps the viewer on click.
 */
export default function PreviewMatchList({
  matches,
  activeIndex,
  query,
  onSelect,
  isSearching = false,
  footnote,
  totalCount,
  className,
}: PreviewMatchListProps) {
  const { t } = useTranslation('board')
  const activeRowRef = useRef<HTMLButtonElement | null>(null)
  // Scrolling this list must not reach the tldraw canvas behind the preview.
  // A React onWheel can't stop tldraw's native listener; this hook can.
  const scrollRef = usePreventWheelPropagation()

  const shown = useMemo(() => matches.slice(0, DEFAULT_MAX_ROWS), [matches])
  const truncated = matches.length > shown.length

  // Keep the active row in view as the user steps through with the chevrons or
  // moves between pages — otherwise the list and the canvas disagree.
  // The optional call is for jsdom, which has no scrollIntoView — without it
  // every suite that mounts a preview with search open crashes on this effect.
  useEffect(() => {
    activeRowRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIndex])

  return (
    <div
      // Width and borders are the caller's call — a side panel and a floating
      // dropdown want different ones, and Tailwind can't reliably override them.
      className={clsx('flex flex-col min-h-0 bg-white dark:bg-dark-card', className)}
      data-testid="preview-match-list"
      // The board renders previews inside the tldraw tree, which otherwise
      // treats these clicks as canvas drags.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-dark-border text-xs font-medium text-gray-600 dark:text-dark-text-muted">
        {isSearching ? (
          <>
            <Loader2 className="w-3 h-3 animate-spin" />
            {t('preview_search.searching')}
          </>
        ) : (
          t('preview_search.matches_count', { count: totalCount ?? matches.length })
        )}
      </div>

      {!isSearching && matches.length === 0 ? (
        <p className="px-3 py-4 text-xs text-gray-500 dark:text-dark-text-muted">
          {t('preview_search.no_matches')}
        </p>
      ) : (
        <ul ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          {shown.map((match, index) => {
            const isActive = index === activeIndex
            const startsGroup =
              !!match.locationLabel &&
              (index === 0 || shown[index - 1].locationLabel !== match.locationLabel)
            return (
              <li key={match.id}>
                {startsGroup && (
                  <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {match.locationLabel}
                  </p>
                )}
                <button
                  type="button"
                  ref={isActive ? activeRowRef : undefined}
                  onClick={() => onSelect(index)}
                  aria-current={isActive}
                  className={clsx(
                    'block w-full text-left px-3 py-1.5 text-xs leading-snug break-words',
                    isActive
                      ? 'bg-brand/10 dark:bg-brand/20 text-gray-900 dark:text-gray-100'
                      : 'text-gray-600 dark:text-dark-text-muted hover:bg-gray-100 dark:hover:bg-dark-accent'
                  )}
                >
                  {markQuery(match.snippet, query)}
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {(truncated || footnote) && (
        <p className="px-3 py-2 border-t border-gray-200 dark:border-dark-border text-[11px] text-gray-500 dark:text-dark-text-muted">
          {/* A caller-side cap wins: `matches.length` is then not the real total,
              so quoting it as one would state a confidently wrong number. */}
          {footnote ??
            t('preview_search.showing_first', { shown: shown.length, total: matches.length })}
        </p>
      )}
    </div>
  )
}
